/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef } from 'react';
import { cn } from '@Utils/index';
import { IComboBoxProps, IDropDownData } from '@Constants/interface';
import { Button } from '@Components/RadixComponents/Button';
import {
  Command,
  CommandGroup,
  CommandItem,
} from '@Components/RadixComponents/Command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@Components/RadixComponents/Popover';
import useDebouncedInput from '@Hooks/useDebouncedInput';
import Icon from '../Icon';
import hasErrorBoundary from '../hasErrorBoundary';
import Searchbar from '../SearchBar';

function Dropdown({
  options = [],
  multiple = false,
  choose = 'id',
  value,
  placeholder,
  onChange,
  onFocus,
  id,
  className,
  disabled,
  isLoading = false,
  dropDownSize = 'drop-lg',
  placeholderClassName,
  hasLeftIcon,
  leftIconName,
  lengthOfOptions = 0,
  style,
  // if checkBox is true then it will show checkbox in dropdown by default it is false
  checkBox = false,
  enableSearchbar = true,
}: IComboBoxProps) {
  const [open, setOpen] = React.useState(false);
  // const [limitError, setLimitError] = React.useState<boolean>(false);
  const [searchText, setSearchText] = React.useState('');
  const [filteredOptionsData, setFilteredOptionsData] = React.useState<any>([]);
  const [dropDownWidth, setDropDownWidth] = React.useState<number | undefined>(
    0,
  );

  const handleSelect = (currentValue: any) => {
    if (onFocus) onFocus();

    if (multiple) {
      const selectedValues = Array.isArray(value) ? [...value] : [];
      const selectedIndex = selectedValues.indexOf(currentValue);
      if (selectedIndex === -1) {
        selectedValues.push(currentValue);
      } else {
        selectedValues.splice(selectedIndex, 1);
      }
      if (onChange) {
        // if there is limit of selection

        if (lengthOfOptions !== 0) {
          if (selectedValues.length > lengthOfOptions) {
            return;
          }
        }
        onChange(selectedValues);
      }
      // setValue(selectedValues);
    } else {
      const selectedValue = currentValue === value ? '' : currentValue;
      // setValue(selectedValue);
      if (onChange) {
        onChange(selectedValue);
      }
      setOpen(false);
    }
  };

  // for search
  const [searchTextData, handleChangeData] = useDebouncedInput({
    ms: 400,
    init: searchText,
    onChange: debouncedEvent => setSearchText(debouncedEvent.target.value),
  });

  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    setDropDownWidth(triggerRef.current?.clientWidth);
  }, [triggerRef.current?.clientWidth]);

  useEffect(() => {
    const filteredOptions = options?.filter(option =>
      option?.label?.toLowerCase()?.includes(searchText?.trim()?.toLowerCase()),
    );
    setFilteredOptionsData(filteredOptions);
  }, [searchText]);

  useEffect(() => {
    setFilteredOptionsData(options);
  }, [options]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild ref={triggerRef}>
        <Button
          id={id}
          variant="dropDown"
          size={dropDownSize}
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          className={cn(
            'naxatw-group naxatw-flex naxatw-items-center naxatw-justify-between naxatw-gap-2 naxatw-bg-white hover:naxatw-border-secondary-500 disabled:!naxatw-cursor-not-allowed',
            className,
          )}
          onClick={() => setOpen(true)}
        >
          <>
            <div className="naxatw-flex naxatw-items-center naxatw-justify-center naxatw-gap-2">
              {hasLeftIcon && (
                <Icon
                  name={leftIconName ?? ''}
                  className="naxatw-flex naxatw-h-6 naxatw-w-6 naxatw-shrink-0 naxatw-items-center naxatw-justify-center naxatw-text-[24px] naxatw-font-light naxatw-leading-6 naxatw-text-matt-200 naxatw-duration-200 group-hover:naxatw-text-secondary-500"
                />
              )}
              {multiple ? (
                <div className="naxatw-flex naxatw-flex-wrap">
                  {Array.isArray(value) && value.length > 0 ? (
                    value.length === 1 ? (
                      <span className="naxatw-body-sm  naxatw-line-clamp-1  naxatw-text-start naxatw-text-matt-200">
                        {options?.find(
                          (option: IDropDownData) =>
                            option[choose as keyof IDropDownData] === value[0],
                        )?.label || '1 Selected'}
                      </span>
                    ) : (
                      <span className="naxatw-line-clamp-1 naxatw-text-start naxatw-font-medium">
                        {value.length} Selected
                      </span>
                    )
                  ) : (
                    <p
                      className={cn(
                        'naxatw-body-sm  naxatw-line-clamp-1  naxatw-text-start naxatw-text-[#667085]',
                        placeholderClassName,
                      )}
                    >
                      {placeholder || 'Choose'}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  {value ? (
                    <p className="naxatw-body-sm  naxatw-line-clamp-1  naxatw-text-start naxatw-text-matt-200">
                      {options.find(
                        (option: IDropDownData) =>
                          option[choose as keyof IDropDownData] === value,
                      )?.label || 'No Name Found'}
                    </p>
                  ) : (
                    <p
                      className={cn(
                        'naxatw-body-sm  naxatw-line-clamp-1  naxatw-text-start naxatw-text-matt-200',
                        placeholderClassName,
                      )}
                    >
                      {placeholder || 'Choose'}
                    </p>
                  )}
                </>
              )}
            </div>
            <Icon
              name={open ? 'expand_less' : 'expand_more'}
              className="naxatw-flex naxatw-h-6 naxatw-w-6 naxatw-shrink-0 naxatw-items-center naxatw-justify-center naxatw-text-[24px] naxatw-font-light naxatw-leading-6 naxatw-text-matt-200 naxatw-duration-200 group-hover:naxatw-text-secondary-500"
            />
          </>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="naxatw-bg-white naxatw-p-[0px]"
        style={{ width: `${dropDownWidth}px`, ...style }}
      >
        {enableSearchbar ? (
          <Searchbar
            value={searchTextData}
            onChange={handleChangeData}
            isSmall
            className="searchbar-dropdown !naxatw-py-3 focus:!naxatw-border-grey-600"
            wrapperStyle="!naxatw-h-auto"
          />
        ) : null}
        <div className="scrollbar search-scrollbar naxatw-block naxatw-max-h-[10rem] naxatw-overflow-y-auto">
          <Command className="naxatw-m-0 naxatw-p-0">
            {isLoading && <p>Loading ...</p>}
            <CommandGroup className="">
              {filteredOptionsData.length ? (
                filteredOptionsData?.map((option: IDropDownData) => (
                  <CommandItem
                    key={option.value?.toString()}
                    onSelect={() =>
                      handleSelect(option[choose as keyof IDropDownData])
                    }
                    className="naxatw-flex naxatw-cursor-pointer naxatw-items-center naxatw-gap-[0.15rem]"
                  >
                    {!checkBox ? (
                      <Icon
                        name="done"
                        className={`naxatw-text-[20px] ${
                          !multiple
                            ? value === option[choose as keyof IDropDownData]
                              ? 'naxatw-opacity-100'
                              : 'naxatw-opacity-0'
                            : Array.isArray(value) &&
                                value.includes(
                                  option[choose as keyof IDropDownData],
                                )
                              ? 'naxatw-opacity-100'
                              : 'naxatw-opacity-0'
                        }`}
                      />
                    ) : (
                      <Icon
                        name={
                          !multiple
                            ? value === option[choose as keyof IDropDownData]
                              ? 'check_box'
                              : 'check_box_outline_blank'
                            : Array.isArray(value) &&
                                value.includes(
                                  option[choose as keyof IDropDownData],
                                )
                              ? 'check_box'
                              : 'check_box_outline_blank'
                        }
                        className="naxatw-text-[20px]"
                      />
                    )}
                    <span>{option.label || '-'}</span>
                  </CommandItem>
                ))
              ) : (
                <div className="naxatw-body-sm naxatw-line-clamp-1 naxatw-flex naxatw-h-[4.25rem] naxatw-items-center  naxatw-justify-center  naxatw-text-start naxatw-text-matt-200">
                  No Data Found.
                </div>
              )}
            </CommandGroup>
          </Command>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default hasErrorBoundary(Dropdown);
